import { Group, Icon, Label, Stack, Tile, Text, Title } from "@assetwatch/ui";

type PasswordFormRequirements = {
  hasSufficientLength: boolean;
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasNumber: boolean;
  hasSpecialCharacter: boolean;
};

type PasswordRequirementsProps = {
  requirements: PasswordFormRequirements;
  isPassedPasswordHistory?: boolean;
  labels?: PasswordRequirementLabels;
  showPreviousPasswordRequirement?: boolean;
  title?: string;
  variant?: "list" | "panel";
};

type PasswordStrength = "weak" | "fair" | "strong";

type PasswordStrengthLabels = Partial<Record<PasswordStrength, string>>;

export type PasswordRequirementLabels = {
  digitRequirement: string;
  lowercaseRequirement: string;
  uppercaseRequirement: string;
  specialCharacterRequirement: string;
  lengthRequirement: string;
  previousPasswordRequirement: string;
  passwordStrength?: PasswordStrengthLabels;
};

export const DEFAULT_PASSWORD_REQUIREMENT_LABELS: PasswordRequirementLabels = {
  digitRequirement: "contain at least one digit",
  lowercaseRequirement: "contain at least one lowercase letter",
  uppercaseRequirement: "contain at least one uppercase letter",
  specialCharacterRequirement: "contain at least one special character",
  lengthRequirement: "be at least 10 characters in length, but not more than 32",
  previousPasswordRequirement:
    "not one of your previous 12 passwords (verified after submission)",
  passwordStrength: {
    weak: "Weak",
    fair: "Fair",
    strong: "Strong",
  },
};

function getCoreRequirements(requirements: PasswordFormRequirements) {
  return [
    requirements.hasLowercase,
    requirements.hasUppercase,
    requirements.hasNumber,
    requirements.hasSpecialCharacter,
    requirements.hasSufficientLength,
  ];
}

function getPasswordStrength({
  isPassedPasswordHistory,
  requirements,
  showPreviousPasswordRequirement,
}: {
  isPassedPasswordHistory?: boolean;
  requirements: PasswordFormRequirements;
  showPreviousPasswordRequirement: boolean;
}): PasswordStrength | null {
  const coreRequirements = getCoreRequirements(requirements);
  const visibleRequirements = showPreviousPasswordRequirement
    ? [...coreRequirements, isPassedPasswordHistory === true]
    : coreRequirements;

  if (!visibleRequirements.some(Boolean)) {
    return null;
  }

  if (visibleRequirements.every(Boolean)) {
    return "strong";
  }

  const isFair =
    requirements.hasLowercase &&
    requirements.hasUppercase &&
    requirements.hasSufficientLength;

  return isFair ? "fair" : "weak";
}

function getStrengthTone(strength: PasswordStrength) {
  return strength === "weak"
    ? "negative"
    : strength === "fair"
      ? "warning"
      : "positive";
}

function getRequirementIconState(isSatisfied?: boolean) {
  if (isSatisfied === undefined) {
    return {
      icon: "warning" as const,
      tone: "warning" as const,
    };
  }

  return isSatisfied
    ? {
        icon: "circle-check" as const,
        tone: "positive" as const,
      }
    : {
        icon: "circle-xmark-solid" as const,
        tone: "negative" as const,
      };
}

function RequirementRow({
  children,
  isSatisfied,
  variant = "list",
}: {
  children: React.ReactNode;
  isSatisfied?: boolean;
  variant?: "list" | "panel";
}) {
  if (variant === "panel") {
    return (
      <Group align="center" gap={8}>
        <Icon
          aria-hidden
          name="circle-check-regular"
          size="sm"
          tone={isSatisfied ? "positive" : "secondary"}
        />
        <Text as="span" size="sm">
          {children}
        </Text>
      </Group>
    );
  }

  const { icon, tone } = getRequirementIconState(isSatisfied);

  return (
    <Group align="start" gap={8}>
      <Icon aria-hidden name={icon} size="sm" tone={tone} />
      <Text size="sm" tone="subtext">
        {children}
      </Text>
    </Group>
  );
}

export function PasswordRequirements({
  requirements,
  isPassedPasswordHistory,
  labels = DEFAULT_PASSWORD_REQUIREMENT_LABELS,
  showPreviousPasswordRequirement = true,
  title,
  variant = "list",
}: PasswordRequirementsProps) {
  const strength =
    variant === "panel"
      ? getPasswordStrength({
          isPassedPasswordHistory,
          requirements,
          showPreviousPasswordRequirement,
        })
      : null;
  const strengthLabel = strength
    ? (labels.passwordStrength?.[strength] ??
      DEFAULT_PASSWORD_REQUIREMENT_LABELS.passwordStrength?.[strength])
    : undefined;
  const content = (
    <Stack gap={variant === "panel" ? 10 : 8}>
      <RequirementRow isSatisfied={requirements.hasLowercase} variant={variant}>
        {labels.lowercaseRequirement}
      </RequirementRow>
      <RequirementRow isSatisfied={requirements.hasUppercase} variant={variant}>
        {labels.uppercaseRequirement}
      </RequirementRow>
      <RequirementRow isSatisfied={requirements.hasNumber} variant={variant}>
        {labels.digitRequirement}
      </RequirementRow>
      <RequirementRow
        isSatisfied={requirements.hasSpecialCharacter}
        variant={variant}
      >
        {labels.specialCharacterRequirement}
      </RequirementRow>
      <RequirementRow
        isSatisfied={requirements.hasSufficientLength}
        variant={variant}
      >
        {labels.lengthRequirement}
      </RequirementRow>
      {showPreviousPasswordRequirement ? (
        <RequirementRow isSatisfied={isPassedPasswordHistory} variant={variant}>
          {labels.previousPasswordRequirement}
        </RequirementRow>
      ) : null}
    </Stack>
  );

  if (variant === "panel") {
    return (
      <Tile padding="dense" radius="sm" tone="muted" width="full">
        <Stack gap={8}>
          {title || strengthLabel ? (
            <Group justify="between">
              {title ? (
                <Title as="span" size="sm">
                  {title}
                </Title>
              ) : null}
              {strength && strengthLabel ? (
                <Label size="md" tone={getStrengthTone(strength)}>
                  {strengthLabel}
                </Label>
              ) : null}
            </Group>
          ) : null}
          {content}
        </Stack>
      </Tile>
    );
  }

  return content;
}
